import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import {
	CreateOrUpdateData,
	ItemPopover,
	ItemPopoverService,
	PopoverResponseMap,
	Profile,
	ProfileMapScope,
	ProfileServerResponse,
	Scope,
	SearchAdvancedProfile
} from '@interfaces'
import { BehaviorSubject, Observable, forkJoin, map, switchMap } from 'rxjs'
import { normalizeRemoteSearchStringParam } from '../remote-text-search.helper'

@Injectable({
	providedIn: 'root'
})
export class ProfilesService implements ItemPopoverService {
	private http = inject(HttpClient)

	private _creatingProfile: boolean = false
	private creatingProfileSubject = new BehaviorSubject<boolean>(false)
	creatingProfile$ = this.creatingProfileSubject.asObservable()

	url = '/profiles'
	importantScopes = ['SETTINGS_ACCESS', 'SETTINGS_MANAGEMENT']

	// Método que devuelve todos los perfiles
	getAllProfiles(body: SearchAdvancedProfile): Observable<ProfileServerResponse> {
		return this.http.post<ProfileServerResponse>(`${this.url}/all`, body)
	}

	// Método que devuelve los perfiles con paginación
	getProfiles(page = 0, size = 15) {
		let originalResponse: ProfileServerResponse
		// URL para perfiles
		const PROFILES_URL = `/profiles/all`
		const body: SearchAdvancedProfile = {
			page,
			size
		}
		// URL para scopes
		const SCOPES_URL = `/profiles/scopes`

		return this.http.post<ProfileServerResponse>(PROFILES_URL, body).pipe(
			switchMap(response => {
				originalResponse = response
				// Obtener scopes antes de continuar
				const scopes$ = this.http.get<Scope[]>(SCOPES_URL)

				// Combina el observable de perfiles con el de scopes
				return forkJoin({
					profiles: response.content, // Si response.content es un observable
					scopes: scopes$
				}).pipe(
					map(({ profiles, scopes }) => {
						const formatedContent = response.content.map(profile => {
							const formatedScopes = profile.scopes.map((scope, index) => {
								const scopeFound = scopes.filter(s => s.value === scope.scope)

								return {
									id: scope.scope,
									key: scopeFound[0].key,
									value: scopeFound[0].key,
									important: this.importantScopes.includes(scopeFound[0].key ?? false),
									active: scope.active
								}
							})

							return {
								id: profile.id,
								active: profile.active,
								description: profile.description,
								superAdmin: profile.superAdmin,
								isAssociated: profile.isAssociated,
								scopes: formatedScopes
							}
						})

						return {
							...originalResponse,
							content: formatedContent
						}
					})
				)
			})
		)
	}

	// Método que devuelve todos los perfiles
	getAllScopes() {
		return this.http.get<Scope[]>('/profiles/scopes').pipe(
			map((scopes: Scope[]) => {
				return scopes.map((s: Scope) => ({
					id: s.value,
					key: s.key ?? '',
					value: s.key ?? '',
					important: this.importantScopes.includes(s.key ?? ''),
					active: false
				}))
			})
		)
	}

	// Método para crear o actualizar un perfil, se diferencia la acción si trae id o no
	createProfile(data: CreateOrUpdateData): Observable<Profile> {
		return this.http.post<Profile>(`${this.url}`, data)
	}

	// Método para crear o actualizar un perfil, se diferencia la acción si trae id o no
	updateProfile(data: ProfileMapScope): Observable<Profile> {
		return this.http.put<Profile>(`${this.url}`, this.convertProfile(data))
	}

	// Método para eliminar el perfil pasado como parámetro
	deleteProfile(profileIds: number[]): Observable<void> {
		return this.http.delete<void>(`${this.url}`, { body: profileIds })
	}

	// Resetea el estado de la creación para indicar que ya no se está creando ningún perfil
	resetCreatingState() {
		this._creatingProfile = false
		this.creatingProfileSubject.next(this._creatingProfile)
	}

	// Cambia el estado de si se está creando un perfil
	toogleCreatingProfile() {
		this._creatingProfile = !this._creatingProfile
		this.creatingProfileSubject.next(this._creatingProfile)
	}

	convertProfile(profile: ProfileMapScope): Profile {
		return {
			id: profile.id,
			isAssociated: profile.isAssociated,
			description: profile.description,
			scopes: profile.scopes.map((scope: ItemPopover) => ({
				scope: scope.id
			}))
		}
	}

	getItemPopoverData(
		page: number,
		size: number,
		type: 'PROFILES' | 'SCOPES',
		params: SearchAdvancedProfile
	): Observable<PopoverResponseMap> {
		const body: SearchAdvancedProfile = {
			page,
			size,
			active: true,
			...params,
			searchString: normalizeRemoteSearchStringParam(params?.searchString)
		}
		if (type === 'PROFILES') {
			return this.getProfilesItemPopover(body)
		} else {
			return this.getScopesItemPopover()
		}
	}

	getProfilesItemPopover(body: SearchAdvancedProfile): Observable<PopoverResponseMap> {
		return this.getAllProfiles(body).pipe(
			map(response => ({
				...response,
				content: response.content
					.filter(profile => !profile.superAdmin)
					.map(
						({ id, description }): ItemPopover => ({
							id: id,
							value: description,
							active: false
						})
					)
			}))
		)
	}

	getScopesItemPopover(): Observable<PopoverResponseMap> {
		return this.getAllScopes().pipe(
			map(response => ({
				number: 1,
				numberOfElements: response.length,
				totalElements: response.length,
				totalPages: 1,
				content: response.map(
					({ id, key, active }): ItemPopover => ({
						id: id,
						value: key,
						key: key,
						active: active
					})
				)
			}))
		)
	}
}
